// =============================================================================
// WEBRTC SERVICE
// =============================================================================
// Manages WebRTC peer connections for voice/video calls
// =============================================================================

import { socketService } from '../api/socket.js';

// =============================================================================
// TYPES
// =============================================================================

export interface WebRTCCallbacks {
  onRemoteStream: (userId: string, stream: MediaStream) => void;
  onRemoteStreamRemoved: (userId: string) => void;
  onConnectionStateChange: (userId: string, state: RTCPeerConnectionState) => void;
  onIceConnectionStateChange: (userId: string, state: RTCIceConnectionState) => void;
}

// =============================================================================
// WEBRTC SERVICE CLASS
// =============================================================================

class WebRTCService {
  private peerConnections = new Map<string, RTCPeerConnection>();
  private localStream: MediaStream | null = null;
  private iceServers: RTCIceServer[] = [];
  private currentCallId: string | null = null;
  private callbacks: WebRTCCallbacks | null = null;
  private pendingIceCandidates = new Map<string, RTCIceCandidateInit[]>();

  // Background-blur pipeline state. When blur is on, the raw camera track is
  // kept aside (cameraTrack) and a canvas-derived blurred track is sent in its
  // place via replaceTrack. Toggling off swaps the camera track back and tears
  // the pipeline down.
  private blurEnabled = false;
  private cameraTrack: MediaStreamTrack | null = null;
  private blurTrack: MediaStreamTrack | null = null;
  private blurRafId: number | null = null;
  private blurVideoEl: HTMLVideoElement | null = null;

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  /**
   * Set ICE servers for peer connections
   */
  setIceServers(servers: RTCIceServer[]): void {
    this.iceServers = servers;
  }

  /**
   * Set callback handlers for WebRTC events
   */
  setCallbacks(callbacks: WebRTCCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Set current call ID for signaling
   */
  setCurrentCallId(callId: string | null): void {
    this.currentCallId = callId;
  }

  // ---------------------------------------------------------------------------
  // Local Media
  // ---------------------------------------------------------------------------

  /**
   * Acquire local media stream (camera/microphone)
   */
  async acquireLocalMedia(video: boolean): Promise<MediaStream> {
    if (this.localStream) {
      return this.localStream;
    }

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: video
        ? {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 60 },
          }
        : false,
    });

    return this.localStream;
  }

  /**
   * Get current local stream
   */
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Toggle local video track
   */
  toggleLocalVideo(enabled: boolean): void {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Toggle local audio track
   */
  toggleLocalAudio(enabled: boolean): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Flip between front and back cameras for a video call.
   *
   * Acquires a new video track from the next available camera, swaps it into
   * every peer connection via the sender's replaceTrack (no renegotiation), and
   * swaps it into the local stream so the self-view follows. Returns the updated
   * local stream, or null if there is no second camera / no video to flip.
   */
  async flipCamera(): Promise<MediaStream | null> {
    if (!this.localStream) {
      return null;
    }

    const currentVideoTrack = this.localStream.getVideoTracks()[0];
    if (!currentVideoTrack) {
      return null;
    }

    // Find a different camera. Prefer an explicit second device; fall back to
    // toggling facingMode so mobile front/back still flips when device labels
    // are withheld (no camera permission to enumerate labels).
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((d) => d.kind === 'videoinput');
    const currentDeviceId = currentVideoTrack.getSettings().deviceId;

    let constraints: MediaTrackConstraints;
    if (cameras.length > 1 && currentDeviceId) {
      const next =
        cameras.find((c) => c.deviceId !== currentDeviceId) ?? cameras[0];
      constraints = { deviceId: { exact: next.deviceId } };
    } else {
      const facing = currentVideoTrack.getSettings().facingMode;
      constraints = { facingMode: facing === 'environment' ? 'user' : 'environment' };
    }

    const replacement = await navigator.mediaDevices.getUserMedia({
      video: constraints,
      audio: false,
    });
    const newVideoTrack = replacement.getVideoTracks()[0];
    if (!newVideoTrack) {
      return null;
    }

    // Carry the current enabled state across the swap so flipping while the
    // camera is off does not silently re-enable it.
    newVideoTrack.enabled = currentVideoTrack.enabled;

    await Promise.all(
      [...this.peerConnections.values()].map(async (pc) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(newVideoTrack);
        }
      })
    );

    currentVideoTrack.stop();
    this.localStream.removeTrack(currentVideoTrack);
    this.localStream.addTrack(newVideoTrack);

    return this.localStream;
  }

  /**
   * Whether the background-blur effect is currently applied to the local video.
   */
  isBlurEnabled(): boolean {
    return this.blurEnabled;
  }

  /**
   * Toggle a one-tap background blur on the local camera. When enabling, the
   * raw camera frames are drawn to an off-screen canvas with a blur filter and
   * the canvas-captured track is swapped into every peer connection (and the
   * local stream) via replaceTrack - the same no-renegotiation path flip-camera
   * uses. Disabling swaps the original camera track back and tears the pipeline
   * down. Returns the updated local stream, or null if there is no video to
   * blur or the canvas pipeline is unsupported (caller leaves the camera as-is).
   */
  async toggleBackgroundBlur(enabled: boolean): Promise<MediaStream | null> {
    if (!this.localStream) return null;
    if (enabled === this.blurEnabled) return this.localStream;

    if (enabled) {
      const cameraTrack = this.localStream.getVideoTracks()[0];
      if (!cameraTrack) return null;

      const blurTrack = this.startBlurPipeline(cameraTrack);
      if (!blurTrack) return null;

      blurTrack.enabled = cameraTrack.enabled;
      await this.swapLocalVideoTrack(cameraTrack, blurTrack, { stopOld: false });

      this.cameraTrack = cameraTrack;
      this.blurTrack = blurTrack;
      this.blurEnabled = true;
      return this.localStream;
    }

    // Disable: swap the original camera track back and stop the blurred one.
    const cameraTrack = this.cameraTrack;
    const blurTrack = this.blurTrack;
    if (!cameraTrack || !blurTrack) {
      this.stopBlurPipeline();
      this.blurEnabled = false;
      return this.localStream;
    }

    cameraTrack.enabled = blurTrack.enabled;
    await this.swapLocalVideoTrack(blurTrack, cameraTrack, { stopOld: true });
    this.stopBlurPipeline();
    this.cameraTrack = null;
    this.blurTrack = null;
    this.blurEnabled = false;
    return this.localStream;
  }

  /**
   * Replace one local video track with another across every peer connection
   * and in the local stream, mirroring the flip-camera swap. stopOld stops the
   * outgoing track (used when discarding the blurred canvas track); the camera
   * track is kept alive across a blur-enable so it can be restored later.
   */
  private async swapLocalVideoTrack(
    oldTrack: MediaStreamTrack,
    newTrack: MediaStreamTrack,
    opts: { stopOld: boolean }
  ): Promise<void> {
    await Promise.all(
      [...this.peerConnections.values()].map(async (pc) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(newTrack);
        }
      })
    );

    if (opts.stopOld) {
      oldTrack.stop();
    }
    if (this.localStream) {
      this.localStream.removeTrack(oldTrack);
      this.localStream.addTrack(newTrack);
    }
  }

  /**
   * Spin up the canvas blur pipeline from a source camera track and return a
   * captured, blurred video track. Returns null if canvas capture is not
   * available in this browser. A privacy-style full-frame blur via the 2D
   * context filter - solid and dependency-free; ML person-segmentation (blur
   * only behind the subject) is a later enhancement.
   */
  private startBlurPipeline(sourceTrack: MediaStreamTrack): MediaStreamTrack | null {
    const settings = sourceTrack.getSettings();
    const width = settings.width || 640;
    const height = settings.height || 480;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof canvas.captureStream !== 'function') {
      return null;
    }

    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([sourceTrack]);
    void video.play().catch(() => {});
    this.blurVideoEl = video;

    const blurPx = Math.max(8, Math.round(width / 50));
    const draw = () => {
      if (video.readyState >= 2) {
        ctx.filter = `blur(${blurPx}px)`;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      this.blurRafId = requestAnimationFrame(draw);
    };
    this.blurRafId = requestAnimationFrame(draw);

    const captured = canvas.captureStream(24);
    return captured.getVideoTracks()[0] || null;
  }

  /**
   * Tear down the blur pipeline (animation loop + hidden source video).
   */
  private stopBlurPipeline(): void {
    if (this.blurRafId !== null) {
      cancelAnimationFrame(this.blurRafId);
      this.blurRafId = null;
    }
    if (this.blurVideoEl) {
      this.blurVideoEl.srcObject = null;
      this.blurVideoEl = null;
    }
  }

  /**
   * List available audio output devices, so the in-call speaker control can
   * route between them via setSinkId. Empty when the browser withholds output
   * devices or none are enumerable; callers treat that as "default only".
   */
  async getAudioOutputDevices(): Promise<MediaDeviceInfo[]> {
    if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
      return [];
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((d) => d.kind === 'audiooutput');
    } catch {
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Peer Connection Management
  // ---------------------------------------------------------------------------

  /**
   * Create a new peer connection for a remote user
   */
  private createPeerConnection(remoteUserId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({
      iceServers: this.iceServers,
      iceCandidatePoolSize: 10,
    });

    // Add local tracks to connection
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        pc.addTrack(track, this.localStream!);
      });
    }

    // ICE candidate handler - trickle ICE to remote peer
    pc.onicecandidate = (event) => {
      if (event.candidate && this.currentCallId) {
        socketService.sendICECandidate({
          callId: this.currentCallId,
          toUserId: remoteUserId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    // Remote stream handler
    pc.ontrack = (event) => {
      const [remoteStream] = event.streams;
      if (remoteStream && this.callbacks) {
        this.callbacks.onRemoteStream(remoteUserId, remoteStream);
      }
    };

    // Connection state changes
    pc.onconnectionstatechange = () => {
      this.callbacks?.onConnectionStateChange(remoteUserId, pc.connectionState);
    };

    // ICE connection state changes
    pc.oniceconnectionstatechange = () => {
      this.callbacks?.onIceConnectionStateChange(remoteUserId, pc.iceConnectionState);
    };

    this.peerConnections.set(remoteUserId, pc);
    return pc;
  }

  /**
   * Get or create peer connection for a remote user
   */
  private getOrCreatePeerConnection(remoteUserId: string): RTCPeerConnection {
    let pc = this.peerConnections.get(remoteUserId);
    if (!pc) {
      pc = this.createPeerConnection(remoteUserId);
    }
    return pc;
  }

  // ---------------------------------------------------------------------------
  // Signaling - Offer/Answer
  // ---------------------------------------------------------------------------

  /**
   * Create and send an SDP offer to a remote user
   */
  async createOffer(remoteUserId: string): Promise<void> {
    const pc = this.getOrCreatePeerConnection(remoteUserId);

    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });

    await pc.setLocalDescription(offer);

    if (this.currentCallId && offer.sdp) {
      socketService.sendSDP({
        callId: this.currentCallId,
        toUserId: remoteUserId,
        type: 'offer',
        sdp: offer.sdp,
      });
    }
  }

  /**
   * Handle an incoming SDP offer and send answer
   */
  async handleRemoteOffer(fromUserId: string, sdp: string): Promise<void> {
    const pc = this.getOrCreatePeerConnection(fromUserId);

    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp }));

    // Process any pending ICE candidates
    await this.processPendingIceCandidates(fromUserId, pc);

    // Create and send answer
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    if (this.currentCallId && answer.sdp) {
      socketService.sendSDP({
        callId: this.currentCallId,
        toUserId: fromUserId,
        type: 'answer',
        sdp: answer.sdp,
      });
    }
  }

  /**
   * Handle an incoming SDP answer
   */
  async handleRemoteAnswer(fromUserId: string, sdp: string): Promise<void> {
    const pc = this.peerConnections.get(fromUserId);
    if (!pc) {
      return;
    }

    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }));

    // Process any pending ICE candidates
    await this.processPendingIceCandidates(fromUserId, pc);
  }

  // ---------------------------------------------------------------------------
  // Signaling - ICE Candidates
  // ---------------------------------------------------------------------------

  /**
   * Handle an incoming ICE candidate
   */
  async handleRemoteICE(fromUserId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const pc = this.peerConnections.get(fromUserId);

    // If remote description not set yet, queue the candidate
    if (!pc || !pc.remoteDescription) {
      const pending = this.pendingIceCandidates.get(fromUserId) || [];
      pending.push(candidate);
      this.pendingIceCandidates.set(fromUserId, pending);
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {
      // ICE candidate addition can fail for non-critical reasons
    }
  }

  /**
   * Process any queued ICE candidates after remote description is set
   */
  private async processPendingIceCandidates(
    userId: string,
    pc: RTCPeerConnection
  ): Promise<void> {
    const pending = this.pendingIceCandidates.get(userId);
    if (!pending || pending.length === 0) return;

    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        // ICE candidate addition can fail for non-critical reasons
      }
    }

    this.pendingIceCandidates.delete(userId);
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  /**
   * Close peer connection for a specific user
   */
  closePeerConnection(userId: string): void {
    const pc = this.peerConnections.get(userId);
    if (pc) {
      pc.close();
      this.peerConnections.delete(userId);
    }
    this.pendingIceCandidates.delete(userId);
    this.callbacks?.onRemoteStreamRemoved(userId);
  }

  /**
   * Cleanup all WebRTC resources
   */
  cleanup(): void {
    // Tear down the blur pipeline and stop its kept-aside camera track.
    this.stopBlurPipeline();
    if (this.blurEnabled && this.cameraTrack) {
      this.cameraTrack.stop();
    }
    this.cameraTrack = null;
    this.blurTrack = null;
    this.blurEnabled = false;

    // Stop local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.localStream = null;
    }

    // Close all peer connections
    this.peerConnections.forEach((pc, userId) => {
      pc.close();
      this.callbacks?.onRemoteStreamRemoved(userId);
    });
    this.peerConnections.clear();

    // Clear state
    this.currentCallId = null;
    this.pendingIceCandidates.clear();
  }

  // ---------------------------------------------------------------------------
  // Debug
  // ---------------------------------------------------------------------------

  /**
   * Get connection stats for debugging
   */
  async getStats(userId: string): Promise<RTCStatsReport | null> {
    const pc = this.peerConnections.get(userId);
    if (!pc) return null;

    return pc.getStats();
  }
}

// =============================================================================
// EXPORT SINGLETON
// =============================================================================

export const webrtcService = new WebRTCService();
